import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  PieChart, Pie, AreaChart, Area,
} from "recharts";
import { money } from "@/lib/format";
import { useChartTheme } from "@/lib/chartTheme";
import { Inbox } from "lucide-react";

// Cohesive engineering chart palette (see /app/docs/design-guidelines.json).
export const CHART_PALETTE = ["#003E87", "#F4C400", "#10B981", "#6366F1", "#EC4899", "#F97316", "#14B8A6", "#8B5CF6"];

export function ChartEmpty({ message = "No data yet", icon: Icon = Inbox }) {
  return (
    <div className="h-40 flex flex-col items-center justify-center text-slate-400" data-testid="chart-empty">
      <Icon className="w-6 h-6 mb-2 text-slate-300" />
      <div className="text-xs">{message}</div>
    </div>
  );
}

function TipCard({ active, payload, valueFmt = money }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-xl px-3 py-2 text-xs">
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.payload?.color || p.color || p.fill }} />
          <span className="text-slate-600">{p.payload?.name || p.name}</span>
          <span className="ml-auto font-mono font-semibold text-slate-900 tabular-nums">{valueFmt(p.payload?.amount ?? p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Waterfall bridge: steps are additive [{name, amount, color}]; a final "Price" total bar is appended.
export function CostWaterfall({ steps = [], height = 220, valueFmt = money }) {
  const ct = useChartTheme();
  const clean = steps.filter((s) => Number.isFinite(s.amount));
  if (!clean.length) return <ChartEmpty message="Nothing priced yet" />;
  let running = 0;
  const rows = clean.map((s) => {
    const row = { name: s.name, spacer: running, delta: s.amount, amount: s.amount, color: s.color };
    running += s.amount;
    return row;
  });
  rows.push({ name: "Price", spacer: 0, delta: running, amount: running, color: ct.total, isTotal: true });
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: ct.tick }} axisLine={false} tickLine={false} interval={0} />
        <YAxis tick={{ fontSize: 10, fill: ct.tickDim }} axisLine={false} tickLine={false} width={54}
          tickFormatter={(v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${v}`)} />
        <Tooltip content={<TipCard valueFmt={valueFmt} />} cursor={{ fill: ct.cursor }} />
        <Bar dataKey="spacer" stackId="a" fill="transparent" />
        <Bar dataKey="delta" stackId="a" radius={[3, 3, 0, 0]} maxBarSize={60}>
          {rows.map((r, i) => <Cell key={i} fill={r.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Donut with a center callout + side legend.
export function CostDonut({ data = [], height = 200, valueFmt = money, centerLabel = "Total", centerValue }) {
  const clean = data.filter((d) => d.value > 0);
  if (!clean.length) return <ChartEmpty message="No breakdown yet" />;
  const total = clean.reduce((s, d) => s + d.value, 0) || 1;
  const center = centerValue != null ? centerValue : valueFmt(total);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <div className="relative shrink-0 max-w-full" style={{ width: height, height }}>
        <PieChart width={height} height={height}>
            <Pie data={clean} dataKey="value" nameKey="label" innerRadius="65%" outerRadius="88%" paddingAngle={1.5} strokeWidth={0}>
              {clean.map((d, i) => <Cell key={i} fill={d.color || CHART_PALETTE[i % CHART_PALETTE.length]} />)}
            </Pie>
            <Tooltip content={({ active, payload }) => active && payload?.length ? (
              <div className="rounded-lg border border-slate-200 bg-white shadow-xl px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: payload[0].payload.color }} />
                  <span className="text-slate-600">{payload[0].payload.label}</span>
                  <span className="ml-auto font-mono font-semibold text-slate-900 tabular-nums">{valueFmt(payload[0].value)}</span>
                </div>
              </div>
            ) : null} />
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{centerLabel}</div>
          <div className="font-mono font-bold text-slate-900 tabular-nums leading-none" style={{ fontSize: height > 180 ? "1.1rem" : "0.95rem" }}>{center}</div>
        </div>
      </div>
      <div className="flex-1 min-w-0 w-full space-y-1.5">
        {clean.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color || CHART_PALETTE[i % CHART_PALETTE.length] }} />
            <span className="flex-1 text-slate-600 truncate">{d.label}</span>
            <span className="font-mono text-slate-500 tabular-nums">{valueFmt(d.value)}</span>
            <span className="font-mono text-slate-300 tabular-nums w-10 text-right">{Math.round((d.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TrendArea({ data = [], dataKey = "value", xKey = "label", height = 120, color = "#003E87", valueFmt = money }) {
  const ct = useChartTheme();
  if (!data.length) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 9, fill: ct.tickDim }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 9, fill: ct.tickDim }} axisLine={false} tickLine={false} width={40} />
        <Tooltip content={<TipCard valueFmt={valueFmt} />} />
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#grad-${dataKey})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
